import { DateRange } from 'react-day-picker'
import { baseFieldClasses, labelClasses } from '../at-text-field/shared/classes'
import { useState } from 'react'
import { Calendar } from '../at-icon'
import { dateValue, formatValue, Holiday } from './overrides'
import { usePopper } from 'react-popper'
import { FieldHints } from '../at-text-field/shared/field-hints'
import { PortableTextProps } from '@portabletext/react'
import { CalendarDialog } from './calendar'

export * from './overrides'

export interface CalendarParams {
  mode?: 'single' | 'range'
  value?: DateRange | Date
  onChange?: (date: Date | DateRange | undefined) => void
}

export interface AtDatePickerProps extends CalendarParams {
  label?: string
  name: string
  errorText?: PortableTextProps['value']
  holidays?: Holiday
  required?: boolean
  fromDate?: Date
  toDate?: Date
  hasError?: boolean
  helperText?: PortableTextProps['value']
  open?: boolean
  onClick?: (open: boolean) => void
}

export const AtDatePicker = ({
  errorText,
  hasError,
  required,
  holidays,
  fromDate,
  toDate,
  helperText,
  label,
  name,
  value,
  onChange,
  mode = 'single',
  open: controlledOpen,
  onClick,
}: AtDatePickerProps) => {
  const [open, setOpen] = useState(false)
  const [field, setField] = useState<HTMLDivElement | null>(null)
  const [calendar, setCalendar] = useState<HTMLDivElement | null>(null)

  const { styles, attributes } = usePopper(field, calendar, {
    placement: 'bottom-end',
  })

  const isControlled = typeof controlledOpen === 'boolean'

  return (
    <div>
      <div ref={setField}>
        <label>
          <span className={labelClasses}>
            {label}
            {required && <span className="text-feedback-negative-300 ml-1">*</span>}
          </span>
          <button
            type="button"
            onClick={() => {
              if (isControlled) onClick?.(true)
              else setOpen(true)
            }}
            name={name}
            className={`${baseFieldClasses(hasError)} ${
              open ? 'border-neutrals-800' : ''
            } h-14 cursor-pointer py-2 px-4 flex justify-between items-center mt-1`}
          >
            <span className={`leading-1.5 ${isValidValue(value) ? 'text-neutrals-800' : 'text-neutrals-600'}`}>
              {isValidValue(value) ? formatValue(mode, value, label) : dateValue(new Date())}
            </span>
            <Calendar />
          </button>
        </label>
        <CalendarDialog
          ref={setCalendar}
          styles={styles}
          attributes={attributes}
          open={isControlled ? controlledOpen : open}
          fromDate={fromDate}
          toDate={toDate}
          required={required}
          onClose={() => {
            if (isControlled) onClick?.(false)
            else setOpen(false)
          }}
          mode={mode}
          selected={value}
          onSelect={onChange}
          holidays={holidays}
        />
      </div>
      <div className="mt-1">
        <FieldHints helperText={helperText} errorText={errorText} hasError={hasError} />
      </div>
    </div>
  )
}

const isValidValue = (value?: Date | DateRange) => {
  if (!value) {
    return false
  }

  if (value instanceof Date) {
    return true
  }

  if (value.from instanceof Date) {
    return true
  }

  return false
}
